/**
 * AL Map Banner - Dynamic Location Switcher
 * 
 * Listens to variant changes and updates the map banner to show
 * location-specific images and content based on the selected variant.
 */

class MapBannerLocationSwitcher {
  constructor() {
    this.banners = [];
    this.init();
  }

  init() {
    // Find all map banner sections
    const bannerDataElements = document.querySelectorAll('[data-map-banner-locations]');
    
    bannerDataElements.forEach((dataElement) => {
      try {
        const data = JSON.parse(dataElement.textContent);
        const bannerId = dataElement.getAttribute('data-map-banner-locations');
        const bannerElement = document.querySelector(`#Banner-${bannerId}`);
        
        if (bannerElement && data.locations && data.locations.length > 0) {
          this.banners.push({
            id: bannerId,
            element: bannerElement,
            data: data
          });
        }
      } catch (e) {
        console.error('Error parsing map banner location data:', e);
      }
    });

    // Subscribe to variant change events
    if (this.banners.length > 0 && typeof subscribe === 'function') {
      this.subscribeToVariantChanges();
    }
  }

  subscribeToVariantChanges() {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (event.data && event.data.variant) {
        this.handleVariantChange(event.data.variant);
      }
    });
  }

  /**
   * Extract location from variant
   * Looks for location in variant options (option1, option2, option3)
   */
  extractLocationFromVariant(variant) {
    if (!variant) return null;

    // Check all variant options for location names
    const locationOptions = [
      variant.option1,
      variant.option2, 
      variant.option3
    ].filter(option => option != null);

    // Return the first non-null option
    // You can customize this logic based on your variant structure
    return locationOptions.length > 0 ? locationOptions.join(' ') : null;
  }

  /**
   * Find matching location from banner data
   */
  findMatchingLocation(bannerData, variantLocationText) {
    if (!variantLocationText) return null;

    const searchText = variantLocationText.toLowerCase();

    // Try exact match first
    let match = bannerData.locations.find(loc => 
      loc.name && loc.name.toLowerCase() === searchText
    );

    // If no exact match, try partial match
    if (!match) {
      match = bannerData.locations.find(loc => 
        loc.name && (
          searchText.includes(loc.name.toLowerCase()) || 
          loc.name.toLowerCase().includes(searchText)
        )
      );
    }

    return match;
  }

  /**
   * Update banner content with location data
   */
  updateBanner(banner, location) {
    const { element } = banner;
    
    // Update heading
    const headingElement = element.querySelector('.banner__heading');
    if (headingElement && location.heading) {
      headingElement.textContent = location.heading;
      if (location.headingColor && location.headingColor !== 'rgba(0,0,0,0)') {
        headingElement.style.setProperty('--banner-heading-color', location.headingColor);
      }
      // Update heading size class
      if (location.headingSize) {
        headingElement.className = headingElement.className.replace(/\bh\w+\b/g, '');
        headingElement.classList.add(location.headingSize);
      }
    }

    // Update text
    const textElement = element.querySelector('.banner__text');
    if (textElement && location.text) {
      textElement.querySelector('p').textContent = location.text;
      // Update text style class
      if (location.textStyle) {
        textElement.className = textElement.className.replace(/\b(body|subtitle|caption-with-letter-spacing)\b/g, '');
        textElement.classList.add(location.textStyle);
      }
    }

    // Update button
    const buttonElement = element.querySelector('.banner__box a.button');
    if (buttonElement) {
      if (location.buttonLabel) {
        buttonElement.textContent = location.buttonLabel;
        buttonElement.style.display = '';
        
        if (location.buttonLink) {
          buttonElement.href = location.buttonLink;
          buttonElement.removeAttribute('role');
          buttonElement.removeAttribute('aria-disabled');
        } else {
          buttonElement.setAttribute('role', 'link');
          buttonElement.setAttribute('aria-disabled', 'true');
          buttonElement.removeAttribute('href');
        }

        // Update button style
        if (location.buttonSecondary) {
          buttonElement.classList.add('button--secondary');
          buttonElement.classList.remove('button--primary');
        } else {
          buttonElement.classList.add('button--primary');
          buttonElement.classList.remove('button--secondary');
        }
      } else {
        buttonElement.style.display = 'none';
      }
    }

    // Update images
    this.updateImages(element, location);

    // Add a subtle animation
    element.style.opacity = '0.7';
    setTimeout(() => {
      element.style.transition = 'opacity 0.3s ease';
      element.style.opacity = '1';
    }, 50);
  }

  /**
   * Update banner images
   */
  updateImages(element, location) {
    const mediaElement = element.querySelector('.banner__media');
    if (!mediaElement) return;

    const pictureElement = mediaElement.querySelector('picture');
    if (!pictureElement) return;

    // Only update if location has an image
    if (!location.image) return;

    // Update mobile image source
    const mobileSource = pictureElement.querySelector('source[media="(max-width: 989px)"]');
    if (location.mobileImage && mobileSource) {
      const mobileSrcset = [
        `${location.mobileImage.srcset['400']} 400w`,
        `${location.mobileImage.srcset['600']} 600w`,
        `${location.mobileImage.srcset['800']} 800w`,
        `${location.mobileImage.srcset['1000']} 1000w`
      ].join(', ');
      
      mobileSource.setAttribute('srcset', mobileSrcset);
      mobileSource.setAttribute('width', location.mobileImage.width);
      mobileSource.setAttribute('height', location.mobileImage.height);
    }

    // Update desktop image
    const imgElement = pictureElement.querySelector('img');
    if (imgElement && location.image) {
      const desktopSrcset = [
        `${location.image.srcset['400']} 400w`,
        `${location.image.srcset['600']} 600w`,
        `${location.image.srcset['800']} 800w`,
        `${location.image.srcset['1000']} 1000w`,
        `${location.image.srcset['1500']} 1500w`,
        `${location.image.srcset['2000']} 2000w`,
        `${location.image.srcset['3000']} 3000w`,
        `${location.image.srcset['3840']} 3840w`
      ].join(', ');

      imgElement.setAttribute('srcset', desktopSrcset);
      imgElement.setAttribute('src', location.image.url);
      imgElement.setAttribute('width', location.image.width);
      imgElement.setAttribute('height', location.image.height);
    }
  }

  /**
   * Handle variant change event
   */
  handleVariantChange(variant) {
    const variantLocationText = this.extractLocationFromVariant(variant);
    
    if (!variantLocationText) {
      console.log('No location found in variant options');
      return;
    }

    console.log('Variant location detected:', variantLocationText);

    // Update all banners on the page
    this.banners.forEach(banner => {
      const matchingLocation = this.findMatchingLocation(banner.data, variantLocationText);
      
      if (matchingLocation) {
        console.log('Updating banner with location:', matchingLocation.name);
        this.updateBanner(banner, matchingLocation);
      } else {
        console.log('No matching location found for:', variantLocationText);
      }
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new MapBannerLocationSwitcher();
  });
} else {
  new MapBannerLocationSwitcher();
}
